<template>
  <div
    class="ai-chat-interface"
    :class="{
      'ai-chat-interface--dock-left': desktopDock === 'left',
    }"
  >
    <!-- FAB Button -->
    <v-fab
      v-if="!isExpanded"
      @click="toggleChat"
      color="primary"
      icon="mdi-robot"
      location="bottom start"
      :class="[
        'chat-fab',
        {
          'chat-fab-full-bottom':
            compactBottom || questionBank === 'part-2-video-bank',
        },
      ]"
      size="large"
    />

    <!-- Expanded Chat Interface -->
    <v-card
      v-if="isExpanded"
      class="chat-container"
      elevation="8"
    >
      <!-- Header -->
      <v-card-title
        class="chat-header d-flex align-center justify-space-between pa-3"
      >
        <div class="d-flex align-center header-left flex-shrink-1 min-width-0">
          <v-avatar
            color="primary"
            size="36"
            class="mr-2 mr-md-3 header-avatar flex-shrink-0"
          >
            <v-icon
              color="white"
              size="22"
              >mdi-robot</v-icon
            >
          </v-avatar>
          <div class="d-flex flex-column min-width-0">
            <span class="text-h6 header-title">Isaac - Eye Academy AI</span>
            <small class="subtitle subtitle-mobile"
              >Focused, concise answers with Eye Academy context</small
            >
          </div>
        </div>

        <div class="header-actions d-flex align-center flex-shrink-0">
          <v-btn
            v-if="!isTyping"
            class="mr-1 new-chat-btn"
            variant="tonal"
            size="small"
            color="white"
            prepend-icon="mdi-plus"
            @click="startNewChat"
            :disabled="isTyping"
          >
            <span class="new-chat-text">New chat</span>
          </v-btn>

          <v-btn
            v-else
            class="mr-1 stop-btn"
            size="small"
            color="error"
            prepend-icon="mdi-stop-circle"
            @click="stopStreaming"
          >
            <span class="stop-text">Stop</span>
          </v-btn>

          <v-btn
            @click="toggleChat"
            icon="mdi-close"
            size="small"
            variant="text"
            color="white"
            :aria-label="'Close chat'"
          />
        </div>
      </v-card-title>

      <v-divider />

      <!-- Messages Area -->
      <v-card-text class="chat-messages pa-0">
        <div class="messages-container">
          <div
            v-for="(message, index) in messages"
            :key="index"
            class="message-wrapper"
            :class="[
              message.isUser ? 'user-message' : 'ai-message',
              message.type === 'activity' ? 'activity-message' : '',
            ]"
          >
            <!-- Activity message bubble -->
            <div
              v-if="message.type === 'activity'"
              class="message-bubble activity-bubble"
            >
              <div class="activity-row">
                <v-icon
                  size="16"
                  :color="
                    message.level === 'error'
                      ? 'error'
                      : message.level === 'success'
                      ? 'success'
                      : 'info'
                  "
                  class="mr-2"
                >
                  {{
                    message.level === "error"
                      ? "mdi-alert-circle"
                      : message.level === "success"
                      ? "mdi-check-circle"
                      : "mdi-progress-clock"
                  }}
                </v-icon>
                <span class="activity-text">{{ message.text }}</span>
              </div>
              <div class="message-time">
                {{ formatTime(message.timestamp) }}
              </div>
            </div>

            <!-- Regular user/AI message bubble -->
            <div
              v-else
              class="message-bubble"
              :class="[
                message.isUser ? 'user-bubble' : 'ai-bubble',
                !message.isUser && !message.isStreaming ? 'has-actions' : '',
              ]"
            >
              <!-- Bubble actions (e.g., copy) for AI messages -->
              <div
                v-if="!message.isUser && !message.isStreaming"
                class="bubble-actions"
              >
                <v-tooltip
                  text="Copy"
                  location="top"
                >
                  <template #activator="{ props: tt }">
                    <v-btn
                      v-bind="tt"
                      variant="text"
                      size="x-small"
                      color="primary"
                      :icon="
                        copiedIndex === index ? 'mdi-check' : 'mdi-content-copy'
                      "
                      @click="copyResponseMd(message, index)"
                      :aria-label="'Copy response markdown'"
                    />
                  </template>
                </v-tooltip>
              </div>
              <!-- Reasoning boxes -->
              <!-- <template v-for="(reasoningItem, idx) in (message.reasoningItems || [])">
                <div
                  v-if="!message.isUser && reasoningItem && reasoningItem.content && reasoningItem.content.trim()"
                  :key="idx"
                  class="reasoning-box"
                >
                  <div class="reasoning-header" @click="reasoningItem && (reasoningItem.isExpanded = !reasoningItem.isExpanded)">
                    <v-icon size="16" color="info">mdi-brain</v-icon>
                    <span class="reasoning-title">Reasoning</span>
                    <v-chip v-if="reasoningItem && reasoningItem.isStreaming" size="x-small" color="info" variant="tonal">Streaming...</v-chip>
                    <v-btn
                      v-if="reasoningItem && !reasoningItem.isStreaming"
                      variant="text"
                      size="x-small"
                      :icon="reasoningItem.isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                      class="ml-auto"
                    />
                  </div>
                  <v-expand-transition>
                    <div v-show="reasoningItem && (reasoningItem.isExpanded || reasoningItem.isStreaming)" class="reasoning-content">
                      <div v-html="parseMD((reasoningItem && reasoningItem.content) || '')"></div>
                    </div>
                  </v-expand-transition>
                </div>
              </template> -->

              <!-- File search box -->
              <div
                v-if="!message.isUser && message.fileSearchStatus"
                class="file-search-box mt-2"
              >
                <div class="file-search-header">
                  <v-icon
                    size="16"
                    :color="
                      message.fileSearchStatus === 'completed'
                        ? 'success'
                        : 'info'
                    "
                    >{{
                      message.fileSearchStatus === "completed"
                        ? "mdi-check-circle"
                        : message.fileSearchStatus === "searching"
                        ? "mdi-magnify"
                        : "mdi-progress-clock"
                    }}</v-icon
                  >
                  <span class="file-search-title">Eye Academy Search</span>
                  <v-chip
                    size="x-small"
                    :color="
                      message.fileSearchStatus === 'completed'
                        ? 'success'
                        : 'info'
                    "
                    variant="tonal"
                  >
                    {{
                      message.fileSearchStatus === "completed"
                        ? "Complete"
                        : message.fileSearchStatus === "searching"
                        ? "Searching..."
                        : "In Progress"
                    }}
                  </v-chip>
                </div>
              </div>

              <div
                v-if="message.rawContent && message.rawContent.trim()"
                v-html="
                  message.isStreaming && !isStreamingToIgnored
                    ? parseMD(message.rawContent || '')
                    : message.htmlContent ?? parseMD(message.rawContent || '')
                "
                class="message-content"
              ></div>

              <div class="message-time">
                {{ formatTime(message.timestamp) }}
              </div>

              <!-- Sources (final, after completion) -->
              <div
                v-if="
                  !message.isUser && message.sources && message.sources.length
                "
                class="sources-container"
              >
                <v-btn
                  variant="text"
                  size="small"
                  color="primary"
                  class="sources-toggle"
                  :prepend-icon="
                    message.showSources
                      ? 'mdi-chevron-down'
                      : 'mdi-chevron-right'
                  "
                  @click="message.showSources = !message.showSources"
                >
                  Sources
                </v-btn>

                <v-expand-transition>
                  <div
                    v-show="message.showSources"
                    class="sources-panel"
                  >
                    <div
                      v-for="(src, sIdx) in message.sources"
                      :key="sIdx"
                      class="source-item"
                    >
                      <div
                        class="source-header"
                        @click="src.isExpanded = !src.isExpanded"
                      >
                        <div class="source-title">
                          <v-icon
                            size="16"
                            color="primary"
                            class="mr-1"
                            >mdi-file-document-outline</v-icon
                          >
                          <span class="source-filename">{{
                            src.filename
                          }}</span>
                        </div>
                        <v-icon
                          size="16"
                          color="primary"
                        >
                          {{
                            src.isExpanded
                              ? "mdi-chevron-up"
                              : "mdi-chevron-down"
                          }}
                        </v-icon>
                      </div>

                      <v-expand-transition>
                        <div
                          v-show="src.isExpanded"
                          class="source-details"
                        >
                          Raw text:
                          <!-- <div class="source-meta">
                            <span
                              v-if="typeof src.index === 'number'"
                              class="meta-chip"
                              >index: {{ src.index }}</span
                            >
                            <span
                              v-if="typeof src.score === 'number'"
                              class="meta-chip"
                              >Match score: {{ formatScore(src.score) }}</span
                            >
                          </div> -->
                          <div
                            v-if="src.snippet"
                            class="source-snippet"
                            v-html="parseSnippet(src.snippet)"
                          ></div>
                        </div>
                      </v-expand-transition>
                    </div>
                  </div>
                </v-expand-transition>
              </div>
            </div>
          </div>

          <!-- Typing indicator -->
          <div
            v-if="isTyping && !isStreamingToIgnored"
            class="message-wrapper ai-message"
          >
            <!-- <div class="message-bubble ai-bubble typing-indicator">
              <div class="typing-dots">
                <span></span><span></span><span></span>
              </div>
            </div> -->
            <v-progress-circular
              color="primary"
              indeterminate
              :size="20"
              :width="2"
            ></v-progress-circular>
          </div>
        </div>
      </v-card-text>

      <!-- Input Area -->
      <v-card-actions class="chat-input pa-3">
        <!-- @keydown.enter="sendMessage" -->
        <v-textarea
          v-model="newMessage"
          placeholder="Type your message..."
          variant="outlined"
          density="compact"
          hide-details
          class="flex-grow-1 mr-2 chat-textarea"
          rows="1"
          auto-grow
          max-rows="6"
          rounded="lg"
          @keydown.enter.prevent="sendMessage"
          :disabled="isTyping"
        />
        <v-btn
          @click="sendMessage"
          :disabled="!newMessage.trim() || isTyping"
          color="primary"
          icon="mdi-send"
          size="small"
        />
      </v-card-actions>
    </v-card>
  </div>
</template>

<script setup>
  import { ref, reactive, nextTick, onMounted } from "vue";
  import { getFunctions, httpsCallable } from "firebase/functions";
  import { useMdHelpers } from "@/composables/mdHelpers";
  const { parseMD } = useMdHelpers();

  // Always create the Functions instance with region and reuse it
  const functions = getFunctions(undefined, "europe-west2");

  // UI state
  const isExpanded = ref(false);
  const newMessage = ref("");
  const isTyping = ref(false);
  const isStreamingToIgnored = ref(false); // if user hit Stop, ignore incoming chunks
  const previousResponseId = ref(null);
  const messages = ref([]);
  const copiedIndex = ref(null);

  const props = defineProps({
    currentQuestionData: {
      type: Object,
      default: {},
    },
    desktopDock: {
      type: String,
      default: "right",
      validator: (value) => ["left", "right"].includes(value),
    },
    compactBottom: {
      type: Boolean,
      default: false,
    },
    questionBank: {
      type: String,
      default: "",
    },
    images: {
      type: Array,
      default: () => [],
    },
    imageUrls: {
      type: Array,
      default: () => [],
    },
  });

  // Welcome message
  onMounted(() => {
    messages.value.push({
      rawContent:
        "Hello! I'm Isaac, your Eye Academy AI assistant. I can answer questions about the current question, or help you with your ophthalmology studies in general. How can I help you today?",
      htmlContent: null, // Important: null, so we can fall back to rawContent when streaming in future
      isUser: false,
      timestamp: new Date(),
      sources: [],
      showSources: false,
      reasoningItems: [],
      fileSearchStatus: null,
      isStreaming: false,
    });
  });

  // Helper: add activity as separate message box
  const pushActivity = (text, level = "info") => {
    messages.value.push({
      type: "activity",
      text,
      level, // info | success | error
      isUser: false,
      timestamp: new Date(),
    });
    scheduleScrollToBottom();
  };

  // Throttled scroll (keeps UI smooth during many small deltas)
  let scrollScheduled = false;
  const scheduleScrollToBottom = () => {
    if (scrollScheduled) return;
    scrollScheduled = true;
    requestAnimationFrame(() => {
      scrollScheduled = false;
      scrollToBottom();
    });
  };

  const toggleChat = () => {
    isExpanded.value = !isExpanded.value;
    if (isExpanded.value) {
      nextTick(scrollToBottom);
    }
  };

  const startNewChat = () => {
    previousResponseId.value = null;
    messages.value = [];
    messages.value.push({
      rawContent: "New chat started. How can I help you today?",
      htmlContent: null,
      isUser: false,
      timestamp: new Date(),
      sources: [],
      showSources: false,
      reasoningItems: [],
      fileSearchStatus: null,
      isStreaming: false,
    });
    nextTick(scrollToBottom);
  };

  const stopStreaming = () => {
    // Mark to ignore further chunks for UX responsiveness.
    isStreamingToIgnored.value = true;

    // Finalize the current AI message visually
    for (let i = messages.value.length - 1; i >= 0; i--) {
      const m = messages.value[i];
      if (m && !m.isUser && m.isStreaming) {
        m.isStreaming = false;
        // Mark all reasoning items as not streaming
        if (m.reasoningItems) {
          m.reasoningItems.forEach((r) => {
            r.isStreaming = false;
            r.isExpanded = false;
          });
        }
        // finalize HTML to current raw content so it renders cleanly
        m.htmlContent = parseMD(m.rawContent || "");
        break;
      }
    }
  };

  const copyResponseMd = async (message, index) => {
    try {
      if (!message || message.isUser || message.isStreaming) return;
      const text = String(message.rawContent || "");
      if (!text.trim()) return;
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers/environments
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        try {
          document.execCommand("copy");
        } finally {
          document.body.removeChild(ta);
        }
      }
      copiedIndex.value = index;
      setTimeout(() => {
        if (copiedIndex.value === index) copiedIndex.value = null;
      }, 1500);
    } catch (err) {
      console.error("Failed to copy markdown:", err);
    }
  };

  const extractSources = (response) => {
    if (!response || !Array.isArray(response.output)) return [];
    const sources = [];
    for (const item of response.output) {
      if (item?.type === "file_search_call") {
        const results = item.results || [];
        for (const r of results) {
          if (r.score > 0.75) {
            sources.push({
              file_id: r.file_id ?? r.fileId,
              filename: r.filename ?? r.name,
              index: r.index,
              score: r.score,
              snippet: r.snippet || r.excerpt || r.text || null,
            });
          }
        }
      }
    }
    return sources;
  };

  const parseSnippet = (text) => {
    if (!text) return "";
    return parseMD(String(text));
  };

  const formatScore = (score) => {
    if (typeof score !== "number") return "";
    return score.toFixed(3);
  };

  const toImageObject = (entry) => {
    if (!entry) return null;

    if (typeof entry === "string") {
      return { url: entry, caption: null };
    }

    if (typeof entry === "object") {
      const url =
        entry.url ||
        entry.image_url ||
        entry.imageUrl ||
        entry.downloadURL ||
        entry.downloadUrl ||
        null;
      if (!url || typeof url !== "string") return null;

      const caption =
        typeof entry.caption === "string"
          ? entry.caption
          : typeof entry.text === "string"
          ? entry.text
          : null;

      return { url, caption: caption || null };
    }

    return null;
  };

  const normalizeImages = ({
    explicitImages = [],
    explicitImageUrls = [],
    questionData = {},
  } = {}) => {
    const candidates = [
      explicitImages,
      explicitImageUrls,
      questionData?.images,
      questionData?.imageUrls,
    ];

    let selected = [];
    for (const candidate of candidates) {
      if (!Array.isArray(candidate) || candidate.length === 0) continue;
      selected = candidate;
      break;
    }

    const normalized = [];
    for (const entry of selected) {
      const image = toImageObject(entry);
      if (image) normalized.push(image);
    }

    // Dedupe while preserving order (keep first caption unless later provides one)
    const deduped = [];
    const seen = new Map();
    for (const image of normalized) {
      const key = image.url;
      if (!seen.has(key)) {
        const record = { ...image };
        seen.set(key, record);
        deduped.push(record);
        continue;
      }
      const existing = seen.get(key);
      if (!existing.caption && image.caption) {
        existing.caption = image.caption;
      }
    }

    return deduped;
  };

  const sendMessage = async () => {
    if (!newMessage.value.trim() || isTyping.value) return;

    const userMessage = newMessage.value.trim();

    // Add user message
    messages.value.push({
      rawContent: userMessage,
      htmlContent: null,
      isUser: true,
      timestamp: new Date(),
    });

    newMessage.value = "";
    isTyping.value = true;
    isStreamingToIgnored.value = false;

    nextTick(scrollToBottom);

    // Create a new AI message placeholder for streaming
    const aiMessage = reactive({
      rawContent: "",
      htmlContent: null, // IMPORTANT: null -> so template uses rawContent while streaming
      isUser: false,
      timestamp: new Date(),
      sources: [],
      showSources: false,
      reasoningItems: [], // Array to handle multiple reasoning boxes
      fileSearchStatus: null,
      isStreaming: true,
    });
    messages.value.push(aiMessage);

    try {
      const chatWithAi = httpsCallable(functions, "chatWithAi");
      const questionData = props.currentQuestionData || {};

      const images = normalizeImages({
        explicitImages: props.images,
        explicitImageUrls: props.imageUrls,
        questionData,
      });

      let questionContext = {};

      if (props.questionBank === "part-2-video-bank") {
        questionContext = {
          stationTitle: questionData.title,
          stationQuestions: questionData.questionsParsed,
          images,
          imageUrls: images.map((img) => img.url),
          questionBank: props.questionBank,
        };
      } else {
        questionContext = {
          questionText: questionData.text,
          images,
          imageUrls: images.map((img) => img.url),
          generalExplanationTitle: questionData.answerTitle,
          generalExplanation: questionData.answerExplanationHTML,
          specificExplanation: questionData.questionSpecificExplanationHTML,
          answerOptions: questionData.options,
          questionBank: props.questionBank,
        };
      }

      const payload = {
        userMessage,
        previousResponseId: previousResponseId.value,
        questionContext,
      };

      // Start streaming
      const { stream, data } = await chatWithAi.stream(payload);

      // Consume streaming chunks
      for await (const chunk of stream) {
        if (isStreamingToIgnored.value) continue; // user hit Stop; ignore

        switch (chunk?.type) {
          case "response.created":
          case "response.queued":
          case "response.in_progress":
            // No activity messages for these
            break;

          case "response.output_item.added": {
            if (chunk.item_type === "reasoning") {
              // Add a new reasoning box for this reasoning item
              aiMessage.reasoningItems.push({
                content: "",
                isStreaming: true,
                isExpanded: true, // Show expanded when streaming
                outputIndex: chunk.output_index || 0,
              });
            } else if (chunk.item_type === "file_search_call") {
              aiMessage.fileSearchStatus = "in_progress";
            }
            break;
          }

          case "response.content_part.added":
            // No action needed
            break;

          case "response.output_text.delta": {
            const delta = chunk.delta || "";
            aiMessage.rawContent += delta;
            scheduleScrollToBottom();
            break;
          }

          case "response.output_text.done":
            // Text part done; keep streaming flag until overall completed
            break;

          // File search activity signals
          case "response.file_search_call.in_progress":
            aiMessage.fileSearchStatus = "in_progress";
            break;

          case "response.file_search_call.searching":
            aiMessage.fileSearchStatus = "searching";
            break;

          case "response.file_search_call.completed":
            aiMessage.fileSearchStatus = "completed";
            break;

          // Reasoning summary (safe-to-show)
          case "response.reasoning_summary_text.delta":
            // Find the most recent reasoning item that's streaming
            const currentReasoning = aiMessage.reasoningItems.findLast(
              (r) => r.isStreaming
            );
            if (currentReasoning) {
              currentReasoning.content += chunk.delta || "";
            }
            break;

          case "response.reasoning_summary_text.done":
            // Mark the current reasoning item as done
            const finishedReasoning = aiMessage.reasoningItems.findLast(
              (r) => r.isStreaming
            );
            if (finishedReasoning) {
              finishedReasoning.isStreaming = false;
              finishedReasoning.isExpanded = false; // Collapse when done
            }
            break;

          case "response.completed":
            // No activity message
            break;

          case "response.failed":
          case "error":
            pushActivity(
              "An error occurred while generating the response.",
              "error"
            );
            break;

          default:
            // no-op: ignore other events or add logging if needed
            break;
        }
      }

      // Final data (returns once the callable completes)
      const result = await data;

      if (result?.success) {
        const rawResponse = result.response;
        const outputText = result.output_text || aiMessage.rawContent || "";

        // Finalize HTML at end for better formatting stability
        aiMessage.htmlContent = parseMD(
          outputText || "I don't have an answer right now."
        );
        aiMessage.rawContent = outputText;

        // Extract sources from the final response
        aiMessage.sources = extractSources(rawResponse).map((s) => ({
          ...s,
          isExpanded: false,
        }));
        aiMessage.isStreaming = false;

        // Track for follow-ups
        previousResponseId.value =
          result.previousResponseId || rawResponse?.id || null;
      } else {
        throw new Error("Failed to get AI response");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      aiMessage.rawContent = error.message;
      aiMessage.htmlContent = null;
      aiMessage.sources = [];
      aiMessage.isStreaming = false;
    } finally {
      isTyping.value = false;
      nextTick(scrollToBottom);
    }
  };

  const scrollToBottom = () => {
    const container = document.querySelector(".messages-container");
    if (container) container.scrollTop = container.scrollHeight;
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };
</script>

<style scoped>
  .ai-chat-interface {
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 1000;
    pointer-events: none;
    --ai-chat-left-offset: 88px;
  }

  /* FAB */
  .chat-fab {
    position: fixed !important;
    bottom: 105px !important;
    right: 70px !important;
    pointer-events: auto;
    filter: drop-shadow(0 8px 18px rgba(25, 118, 210, 0.25));
    transition:
      transform 0.2s ease,
      filter 0.2s ease;
  }
  .chat-fab-full-bottom {
    bottom: 25px !important;
  }

  .chat-fab:hover {
    transform: translateY(-2px);
    filter: drop-shadow(0 10px 22px rgba(25, 118, 210, 0.35));
  }
  .chat-fab:active {
    transform: translateY(0);
  }

  /* Container */
  .chat-container {
    position: fixed;
    bottom: 80px;
    right: 80px;
    width: 600px;
    height: 640px;
    display: flex;
    flex-direction: column;
    border-radius: 20px;
    pointer-events: auto;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: saturate(180%) blur(12px);
    box-shadow:
      0 10px 25px rgba(0, 0, 0, 0.08),
      0 1px 0 rgba(255, 255, 255, 0.25) inset;
    overflow: hidden;
    border: 1px solid rgba(21, 101, 192, 0.08);
  }

  @media (min-width: 1271px) {
    .ai-chat-interface--dock-left .chat-fab {
      right: auto !important;
      left: var(--ai-chat-left-offset) !important;
    }

    .ai-chat-interface--dock-left .chat-container {
      right: auto;
      left: var(--ai-chat-left-offset);
    }
  }

  /* Header */
  .chat-header {
    background: linear-gradient(135deg, #1e88e5 0%, #1565c0 100%);
    color: white;
    border-radius: 16px 16px 0 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.15);
    gap: 8px;
    overflow: hidden;
  }
  .header-left {
    min-width: 0;
    overflow: hidden;
  }
  .header-avatar {
    box-shadow: 0 4px 12px rgba(13, 71, 161, 0.35);
    flex-shrink: 0;
  }
  .chat-header .text-h6 {
    font-weight: 700;
    letter-spacing: 0.2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .header-title {
    font-size: 1rem;
  }
  .subtitle {
    opacity: 0.85;
    font-size: 12px;
  }

  .header-actions {
    flex-shrink: 0;
    gap: 4px;
  }
  .header-actions .v-btn {
    pointer-events: auto;
  }
  .new-chat-btn :deep(.v-btn__content) {
    white-space: nowrap;
  }

  /* Mobile responsive header */
  @media (max-width: 600px) {
    .subtitle-mobile {
      display: none !important;
    }
    .new-chat-text,
    .stop-text {
      display: none;
    }
    .new-chat-btn,
    .stop-btn {
      min-width: 40px !important;
      padding: 0 8px !important;
    }
    .new-chat-btn :deep(.v-btn__prepend),
    .stop-btn :deep(.v-btn__prepend) {
      margin-inline-end: 0 !important;
    }
    .header-title {
      font-size: 0.9rem;
    }
    .chat-header {
      padding: 12px !important;
    }
  }

  /* Messages */
  .chat-messages {
    flex: 1;
    overflow: hidden;
    background: radial-gradient(
        1200px 400px at -200px -200px,
        rgba(25, 118, 210, 0.06),
        transparent 60%
      ),
      linear-gradient(180deg, rgba(21, 101, 192, 0.03), transparent 40%);
  }

  .messages-container {
    height: 100%;
    overflow-y: auto;
    padding: 16px 16px 10px 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    scroll-behavior: smooth;
  }

  /* Message wrappers */
  .message-wrapper {
    display: flex;
    width: 100%;
  }
  .user-message {
    justify-content: flex-end;
  }
  .ai-message {
    justify-content: flex-start;
  }

  /* Bubbles */
  .message-bubble {
    max-width: 80%;
    padding: 12px 16px;
    border-radius: 18px;
    word-wrap: break-word;
    position: relative;
    transform: translateZ(0);
    transition:
      transform 0.15s ease,
      box-shadow 0.15s ease,
      background 0.2s ease;
  }
  .user-bubble {
    background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
    color: white;
    border-bottom-right-radius: 6px;
    box-shadow: 0 6px 14px rgba(25, 118, 210, 0.25);
  }
  .user-bubble:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(25, 118, 210, 0.32);
  }

  .ai-bubble {
    background: #ffffff;
    color: #233245;
    border-bottom-left-radius: 6px;
    border: 1px solid #e8eef6;
    box-shadow: 0 4px 10px rgba(18, 38, 63, 0.06);
  }
  .ai-bubble.has-actions {
    padding-top: 28px; /* room for action button */
  }
  .ai-bubble:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 14px rgba(18, 38, 63, 0.09);
  }

  /* Content and time */
  .message-content {
    font-size: 14.5px;
    line-height: 1.6;
    margin-bottom: 6px;
  }
  .bubble-actions {
    position: absolute;
    top: 6px;
    right: 6px;
  }
  .message-time {
    font-size: 11.5px;
    opacity: 0.7;
    text-align: right;
  }
  .ai-bubble .message-time {
    text-align: left;
  }

  /* Reasoning box */
  .reasoning-box {
    margin-bottom: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #fff7e6;
    border: 1px solid #ffe0b3;
  }
  .reasoning-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    cursor: pointer;
    user-select: none;
  }
  .reasoning-header:hover {
    background: rgba(184, 134, 11, 0.05);
    border-radius: 6px;
    margin: -4px;
    padding: 4px;
  }
  .reasoning-title {
    font-weight: 600;
    font-size: 13px;
    color: #b8860b;
  }
  .reasoning-content {
    font-size: 13px;
    color: #6b4c1f;
    line-height: 1.5;
  }

  /* File search box */
  .file-search-box {
    margin-bottom: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #f0f8ff;
    border: 1px solid #c6e2ff;
  }
  .file-search-header {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .file-search-title {
    font-weight: 600;
    font-size: 13px;
    color: #1565c0;
  }

  /* Sources */
  .sources-container {
    margin-top: 6px;
  }
  .sources-toggle {
    min-height: 28px;
    padding: 0 6px;
    margin-left: -6px;
  }
  .sources-panel {
    margin-top: 6px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #f7faff;
    border: 1px solid #e6eef7;
  }
  .source-item + .source-item {
    margin-top: 8px;
  }
  .source-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px;
    border-radius: 8px;
    cursor: pointer;
    user-select: none;
    transition:
      background 0.15s ease,
      box-shadow 0.15s ease;
  }
  .source-header:hover {
    background: #eef5ff;
    box-shadow: inset 0 0 0 1px #e1ecff;
  }
  .source-title {
    font-weight: 600;
    color: #1565c0;
    display: flex;
    align-items: center;
  }
  .source-filename {
    margin-left: 4px;
  }
  .source-details {
    margin-top: 6px;
    padding: 8px 10px 10px 12px;
    border-left: 3px solid #e1ecff;
    background: #ffffff;
    border-radius: 6px;
  }
  .source-meta {
    margin-top: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .meta-chip {
    background: #e8f1fd;
    border: 1px solid #d7e6fb;
    color: #2459a6;
    border-radius: 999px;
    padding: 2px 8px;
    font-size: 11px;
  }
  .source-snippet {
    margin-top: 6px;
    font-size: 13px;
    color: #2d3b50;
    max-height: 220px;
    overflow: auto;
  }

  /* Input area */
  .chat-input {
    border-top: 1px solid rgba(21, 101, 192, 0.08);
    background: rgba(255, 255, 255, 0.9);
    border-radius: 0 0 16px 16px;
    padding: 12px !important;
    display: flex;
    gap: 8px;
    align-items: center;
  }
  .chat-input :deep(.v-field) {
    border-radius: 999px !important;
    box-shadow: 0 3px 10px rgba(18, 38, 63, 0.06);
    transition:
      box-shadow 0.2s ease,
      transform 0.1s ease;
  }
  .chat-input :deep(.v-field:hover) {
    box-shadow: 0 4px 14px rgba(18, 38, 63, 0.09);
  }
  .chat-input :deep(.v-field--focused) {
    box-shadow: 0 6px 18px rgba(25, 118, 210, 0.15);
  }
  .chat-input :deep(.v-btn[icon]) {
    border-radius: 999px;
    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.25);
    transition:
      transform 0.1s ease,
      box-shadow 0.2s ease,
      background 0.2s ease;
  }
  .chat-input :deep(.v-btn[icon]:hover) {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(25, 118, 210, 0.32);
  }
  .chat-input :deep(.v-btn[icon]:disabled) {
    box-shadow: none;
    opacity: 0.5;
  }

  /* Typing indicator */
  .typing-indicator {
    padding: 14px 18px !important;
  }
  .typing-dots {
    display: inline-flex;
    gap: 6px;
    align-items: center;
  }
  .typing-dots span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(135deg, #90caf9, #42a5f5);
    animation: typing 1.6s infinite ease-in-out;
    box-shadow: 0 2px 6px rgba(66, 165, 245, 0.35);
  }
  .typing-dots span:nth-child(2) {
    animation-delay: 0.15s;
  }
  .typing-dots span:nth-child(3) {
    animation-delay: 0.3s;
  }
  @keyframes typing {
    0%,
    60%,
    100% {
      transform: translateY(0);
      opacity: 0.7;
    }
    30% {
      transform: translateY(-7px);
      opacity: 1;
    }
  }

  /* Markdown polishing */
  .message-content :deep(.md-paragraph) {
    margin: 0.5rem 0;
    line-height: 1.6;
  }
  .message-content :deep(.md-paragraph:first-child) {
    margin-top: 0;
  }
  .message-content :deep(.md-paragraph:last-child) {
    margin-bottom: 0;
  }
  .message-content :deep(.md-heading) {
    font-weight: 700;
    margin: 0.75rem 0 0.4rem 0;
    color: #1565c0;
    letter-spacing: 0.2px;
  }
  .message-content :deep(.md-h1) {
    font-size: 1.2rem;
  }
  .message-content :deep(.md-h2) {
    font-size: 1.1rem;
  }
  .message-content :deep(.md-h3) {
    font-size: 1.05rem;
  }
  .message-content :deep(.md-list) {
    margin: 0.5rem 0;
    padding-left: 1.25rem;
  }
  .message-content :deep(.md-list-item) {
    margin-bottom: 0.25rem;
  }

  /* Markdown tables: polished + horizontally scrollable */
  .message-content :deep(.md-table-wrapper),
  .reasoning-content :deep(.md-table-wrapper),
  .source-snippet :deep(.md-table-wrapper) {
    /* In case we ever wrap tables via renderer */
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .message-content :deep(.md-table),
  .reasoning-content :deep(.md-table),
  .source-snippet :deep(.md-table) {
    /* Make the table its own scroll container when needed */
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    max-width: 100%;
    width: max-content; /* allow natural width; scroll if wider than bubble */

    border-collapse: separate;
    border-spacing: 0;
    background: #ffffff;
    border: 1px solid #e6eef7;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(18, 38, 63, 0.06);
    margin: 8px 0;
  }
  .message-content :deep(.md-table thead th),
  .reasoning-content :deep(.md-table thead th),
  .source-snippet :deep(.md-table thead th) {
    position: sticky; /* stays visible while horizontally scrolling */
    top: 0; /* doesn't affect layout here but keeps style consistent */
    background: #f2f7ff;
    color: #2459a6;
    font-weight: 700;
  }
  .message-content :deep(.md-table th),
  .message-content :deep(.md-table td),
  .reasoning-content :deep(.md-table th),
  .reasoning-content :deep(.md-table td),
  .source-snippet :deep(.md-table th),
  .source-snippet :deep(.md-table td) {
    padding: 8px 12px;
    border-right: 1px solid #e6eef7;
    border-bottom: 1px solid #e6eef7;
    text-align: left;
    font-size: 13px;
    white-space: nowrap; /* prefer horizontal scroll over wrapping */
  }
  .message-content :deep(.md-table th:last-child),
  .message-content :deep(.md-table td:last-child),
  .reasoning-content :deep(.md-table th:last-child),
  .reasoning-content :deep(.md-table td:last-child),
  .source-snippet :deep(.md-table th:last-child),
  .source-snippet :deep(.md-table td:last-child) {
    border-right: none;
  }
  .message-content :deep(.md-table tr:last-child td),
  .reasoning-content :deep(.md-table tr:last-child td),
  .source-snippet :deep(.md-table tr:last-child td) {
    border-bottom: none;
  }
  .message-content :deep(.md-table tbody tr:nth-child(odd) td),
  .reasoning-content :deep(.md-table tbody tr:nth-child(odd) td),
  .source-snippet :deep(.md-table tbody tr:nth-child(odd) td) {
    background: #fbfdff;
  }
  .message-content :deep(.md-table tbody tr:hover td),
  .reasoning-content :deep(.md-table tbody tr:hover td),
  .source-snippet :deep(.md-table tbody tr:hover td) {
    background: #f6faff;
  }
  .message-content :deep(.md-table .md-inline-code),
  .reasoning-content :deep(.md-table .md-inline-code),
  .source-snippet :deep(.md-table .md-inline-code) {
    background: #f4f6fa;
    border: 1px solid #e6eaf2;
    border-radius: 4px;
    padding: 1px 4px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
      "Liberation Mono", "Courier New", monospace;
  }

  /* Thin horizontal scrollbar for wide tables */
  .message-content :deep(.md-table::-webkit-scrollbar),
  .reasoning-content :deep(.md-table::-webkit-scrollbar),
  .source-snippet :deep(.md-table::-webkit-scrollbar) {
    height: 8px;
  }
  .message-content :deep(.md-table::-webkit-scrollbar-track),
  .reasoning-content :deep(.md-table::-webkit-scrollbar-track),
  .source-snippet :deep(.md-table::-webkit-scrollbar-track) {
    background: transparent;
  }
  .message-content :deep(.md-table::-webkit-scrollbar-thumb),
  .reasoning-content :deep(.md-table::-webkit-scrollbar-thumb),
  .source-snippet :deep(.md-table::-webkit-scrollbar-thumb) {
    background: rgba(25, 118, 210, 0.28);
    border-radius: 999px;
    border: 2px solid transparent;
    background-clip: padding-box;
  }
  .message-content :deep(.md-table:hover::-webkit-scrollbar-thumb),
  .reasoning-content :deep(.md-table:hover::-webkit-scrollbar-thumb),
  .source-snippet :deep(.md-table:hover::-webkit-scrollbar-thumb) {
    background: rgba(25, 118, 210, 0.45);
  }

  /* Scrollbar */
  .messages-container::-webkit-scrollbar {
    width: 10px;
  }
  .messages-container::-webkit-scrollbar-track {
    background: transparent;
  }
  .messages-container::-webkit-scrollbar-thumb {
    background: rgba(25, 118, 210, 0.25);
    border-radius: 999px;
    border: 3px solid transparent;
    background-clip: padding-box;
  }
  .messages-container:hover::-webkit-scrollbar-thumb {
    background: rgba(25, 118, 210, 0.4);
    border: 2px solid transparent;
  }

  /* Activity message box in the stream (separate cards) */
  .chat-textarea :deep(.v-field) {
    /* Remove pill look when textarea grows */
    border-radius: 12px !important;
  }
  .chat-textarea :deep(.v-field__outline),
  .chat-textarea :deep(.v-field__overlay) {
    border-radius: 12px !important;
  }
  .chat-textarea :deep(.v-field__input) {
    /* Make internal area look flat and comfortable */
    border-radius: 12px !important;
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }
  .chat-textarea :deep(textarea) {
    /* Ensure it scrolls cleanly at the cap */
    max-height: 168px; /* ~6 rows */
    overflow-y: auto !important;
    resize: none;
  }

  .activity-message {
    justify-content: center;
  }
  .activity-bubble {
    background: #f3f8ff;
    border: 1px solid #e0ebff;
    color: #2d3b50;
    box-shadow: 0 3px 8px rgba(18, 38, 63, 0.05);
    border-radius: 12px;
    padding: 8px 12px;
    max-width: 70%;
  }
  .activity-row {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .activity-text {
    font-size: 13px;
  }

  /* Responsive */
  @media (max-width: 1270px) {
    .chat-fab {
      bottom: 105px !important;
      right: 20px !important;
    }

    .chat-fab-full-bottom {
      bottom: 25px !important;
      left: 20px !important;
      right: auto !important;
    }
    .chat-container {
      width: calc(100vw - 24px);
      height: calc(100vh - 120px);
      bottom: 16px;
      left: 12px;
      right: 12px;
      border-radius: 16px;
    }
  }
  @media (max-width: 600px) {
    .chat-fab {
      bottom: 100px !important;
      right: 10px !important;
    }

    .chat-fab-full-bottom {
      bottom: 15px !important;
      left: 10px !important;
      right: auto !important;
    }
    .chat-container {
      width: calc(100vw - 24px);
      height: calc(100vh - 120px);
      bottom: 16px;
      left: 12px;
      right: 12px;
      border-radius: 16px;
    }
  }
</style>
